/**
 * A dropped subtitle file to a `SubtitleDocument`, with the site's own decoder
 * and parsers, called directly rather than through the subtitle worker: the
 * burner needs the document itself on this side (the preview draws from it and
 * the encoding worker is handed a track built from it), and the subtitle
 * worker keeps documents on its side by design.
 *
 * Text formats with their own timing only: SRT, VTT, ASS, SSA, SMI and SBV.
 * Frame-based SUB needs a frame rate prompt this page does not have yet, and
 * TXT/LRC are not subtitles for a video.
 */

import type {
  ParseOptions,
  ParserDefinition,
  ReadFormat,
  SubtitleDocument,
} from '../../../../../contracts/cue-model.ts';
import { createIdAllocator } from '../../../../../src/core/ids/allocator.ts';
import { createCaseMapper } from '../../../../../src/core/text/casing.ts';
import { createMeasure } from '../../../../../src/core/text/measure.ts';
import { assParser, ssaParser } from '../../../../../src/formats/parse/ass.ts';
import { sbvParser } from '../../../../../src/formats/parse/sbv.ts';
import { smiParser } from '../../../../../src/formats/parse/smi.ts';
import { srtParser } from '../../../../../src/formats/parse/srt.ts';
import { vttParser } from '../../../../../src/formats/parse/vtt.ts';
import { decodeBytes } from '../../../../../src/worker/adapter/encoding.ts';

const PARSERS: readonly ParserDefinition[] = [
  srtParser,
  vttParser,
  assParser,
  ssaParser,
  smiParser,
  sbvParser,
];

export const BURNABLE_FORMATS: readonly ReadFormat[] = PARSERS.map((p) => p.format);

export type ReadOutcome =
  | { readonly ok: true; readonly doc: SubtitleDocument; readonly format: ReadFormat }
  | { readonly ok: false; readonly reason: 'not-recognized' | 'parse-failed' | 'empty' };

function sniff(text: string, name: string): ParserDefinition | null {
  let best: ParserDefinition | null = null;
  let bestScore = 0;
  for (const parser of PARSERS) {
    let score = 0;
    try {
      score = parser.sniff(text, name);
    } catch {
      score = 0;
    }
    if (score > bestScore) {
      bestScore = score;
      best = parser;
    }
  }
  return bestScore > 0 ? best : null;
}

let epoch = 0;

export function readSubtitles(bytes: Uint8Array, name: string): ReadOutcome {
  const decoded = decodeBytes({ bytes, filename: name });
  const parser = sniff(decoded.text, name);
  if (!parser) return { ok: false, reason: 'not-recognized' };
  epoch += 1;
  const options: ParseOptions = {
    framerate: null,
    framerateSource: null,
    inputId: `b${epoch}-i0` as ParseOptions['inputId'],
    inputName: name,
    encoding: decoded.encoding,
    defaultResolution: null,
    singleTrack: false,
    retainRaw: false,
  };
  let result: ReturnType<ParserDefinition['parse']>;
  try {
    result = parser.parse(decoded.text, options, {
      measure: createMeasure(),
      alloc: createIdAllocator({ epoch: `b${epoch}`, counter: 0 }),
      casing: createCaseMapper(),
    });
  } catch {
    return { ok: false, reason: 'parse-failed' };
  }
  if (!result.ok) return { ok: false, reason: 'parse-failed' };
  if (result.doc.cues.length === 0) return { ok: false, reason: 'empty' };
  return { ok: true, doc: result.doc, format: parser.format };
}
